/**
 * One custom agent's page.
 *
 * Route: /prioritization/settings/custom-agents/:agentId
 *
 * Its identity — name, what it is for, its type — above the template's common
 * settings page, which renders the type's own panel. The type is fixed once
 * the agent is made (migration 917); renaming and removing are for workspace
 * administrators only, and the server checks the same.
 */

import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { Loader2, Trash2 } from 'lucide-react';
import { Button } from '../../../components/ui/Button';
import { Callout, Field, TextArea, TextInput } from '../../../components/ds';
import { TemplateAgentSettings } from '../../../components/agents/TemplateAgentSettings';
import { getCurrentWorkspaceId } from '../../../lib/supabaseSync';
import { useCustomAgentStore } from '../../../stores/prioritization/customAgentStore';
import { useUserStore } from '../../../stores/prioritization/userStore';
import { AGENT_BEHAVIORS, definitionForCustomAgent } from '../../../lib/ai/agentDefinitions';
import type { CustomAgent } from '../../../lib/customAgentService';

function IdentityCard({ agent, canEdit }: { agent: CustomAgent; canEdit: boolean }) {
  const saveAgent = useCustomAgentStore((s) => s.saveAgent);
  const deleteAgent = useCustomAgentStore((s) => s.deleteAgent);
  const isSaving = useCustomAgentStore((s) => s.isSaving);
  const isDeleting = useCustomAgentStore((s) => s.isDeleting);
  const navigate = useNavigate();

  const [name, setName] = useState(agent.name);
  const [description, setDescription] = useState(agent.description ?? '');
  const [confirmRemove, setConfirmRemove] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setName(agent.name);
    setDescription(agent.description ?? '');
  }, [agent.name, agent.description]);

  const dirty = name.trim() !== agent.name || (description.trim() || null) !== (agent.description ?? null);
  const behavior = AGENT_BEHAVIORS[agent.behaviorKind];

  const handleSave = async () => {
    setError(null);
    const res = await saveAgent({
      id: agent.id,
      workspaceId: agent.workspaceId,
      name: name.trim(),
      description: description.trim() || null,
      behaviorKind: agent.behaviorKind,
    });
    if (!res.success) setError(res.error ?? 'Not saved.');
  };

  const handleRemove = async () => {
    setError(null);
    const res = await deleteAgent(agent.id, agent.workspaceId);
    if (!res.success) {
      setError(res.error ?? 'Not removed.');
      return;
    }
    navigate('/prioritization/settings/custom-agents');
  };

  return (
    <div className="card space-y-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-[var(--text-pri)]">This agent</h3>
          <p className="text-xs text-[var(--text-ter)] mt-0.5">
            Type: <span className="font-medium text-[var(--text-sec)]">{behavior.label}</span>. The type is fixed once an agent is made; everything below can change.
          </p>
        </div>
        {canEdit && !confirmRemove && (
          <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(true)}>
            <Trash2 className="h-3.5 w-3.5" /> Remove
          </Button>
        )}
      </div>

      {error && <Callout tone="coral">{error}</Callout>}

      {confirmRemove && (
        <Callout
          tone="coral"
          title={`Remove ${agent.name}?`}
          action={(
            <div className="flex gap-2">
              <Button variant="ghost" size="sm" onClick={() => setConfirmRemove(false)}>Keep it</Button>
              <Button variant="primary" size="sm" disabled={isDeleting} onClick={() => void handleRemove()}>
                {isDeleting ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : null} Remove
              </Button>
            </div>
          )}
        >
          It stops running and its settings go. Proposals it already made stay on their items, and its decisions stay in the record.
        </Callout>
      )}

      {canEdit ? (
        <div className="space-y-3">
          <Field label="Name" required htmlFor="custom-agent-name">
            <TextInput id="custom-agent-name" value={name} onChange={(e) => setName(e.target.value)} />
          </Field>
          <Field label="What it is for" htmlFor="custom-agent-description">
            <TextArea id="custom-agent-description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          {dirty && (
            <div className="flex justify-end">
              <Button variant="primary" size="sm" disabled={isSaving || !name.trim()} onClick={() => void handleSave()}>
                {isSaving ? 'Saving…' : 'Save'}
              </Button>
            </div>
          )}
        </div>
      ) : (
        agent.description && <p className="text-sm text-[var(--text-sec)]">{agent.description}</p>
      )}
    </div>
  );
}

export default function CustomAgentDetailPage() {
  const { agentId } = useParams<{ agentId: string }>();
  const workspaceId = getCurrentWorkspaceId();
  const can = useUserStore((s) => s.can);
  const agent = useCustomAgentStore((s) => s.agents.find((a) => a.id === agentId) ?? null);
  const fetchAgent = useCustomAgentStore((s) => s.fetchAgent);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!agentId) return;
    void fetchAgent(agentId).finally(() => setLoaded(true));
  }, [agentId, fetchAgent]);

  const def = useMemo(() => (agent ? definitionForCustomAgent(agent) : null), [agent]);

  if (!agent || !def) {
    return (
      <div className="space-y-4">
        <Link to="/prioritization/settings/custom-agents" className="text-xs text-[var(--text-ter)] no-underline">← Custom agents</Link>
        {loaded ? (
          <Callout tone="mustard">This agent does not exist, or it belongs to another workspace.</Callout>
        ) : (
          <div className="card flex items-center gap-2 text-sm text-[var(--text-ter)]"><Loader2 className="h-4 w-4 animate-spin" /> Loading…</div>
        )}
      </div>
    );
  }

  if (workspaceId && agent.workspaceId !== workspaceId) {
    return <Callout tone="mustard">This agent belongs to another workspace. Switch to it to see its settings.</Callout>;
  }

  return <TemplateAgentSettings def={def} identity={<IdentityCard agent={agent} canEdit={can('admin.settings')} />} />;
}
